import type { LatestResultIdentity, LatestResultPayload } from "../types";

const IDENTITY_KEY = "nbti:latest-result:v1:identity";

export function loadLatestResultIdentity(): LatestResultIdentity | undefined {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY);
    return raw ? JSON.parse(raw) as LatestResultIdentity : undefined;
  } catch {
    return undefined;
  }
}

export function saveLatestResultIdentity(identity: LatestResultIdentity) {
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
}

export async function saveLatestResult(payload: LatestResultPayload, identity?: LatestResultIdentity) {
  const requestBody = JSON.stringify({
    ...payload,
    user_ref: identity?.userRef,
    write_token: identity?.writeToken,
    revision: identity?.revision || 0,
  });
  let body: Record<string, unknown> = {};
  let lastError: Error | undefined;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await fetch("/api/latest-test-result", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
      });
      body = await response.json().catch(() => ({})) as Record<string, unknown>;
      if (response.ok) {
        lastError = undefined;
        break;
      }
      const error = new Error(typeof body.error === "string" ? body.error : `save_failed_${response.status}`) as Error & { noRetry?: boolean };
      if (response.status < 500) {
        error.noRetry = true;
        throw error;
      }
      lastError = error;
    } catch (error) {
      if (error instanceof Error && (error as Error & { noRetry?: boolean }).noRetry) throw error;
      lastError = error instanceof Error ? error : new Error("save_failed");
    }
    if (attempt < 2) await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
  }
  if (lastError) throw lastError;
  const nextIdentity: LatestResultIdentity = {
    userRef: String(body.user_ref),
    writeToken: identity?.writeToken || String(body.write_token),
    revision: Number(body.revision),
  };
  if (!nextIdentity.userRef || !nextIdentity.writeToken || !Number.isInteger(nextIdentity.revision)) throw new Error("invalid_save_response");
  saveLatestResultIdentity(nextIdentity);
  return nextIdentity;
}

export function createSubmissionId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `submission_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

export function createBootstrapToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}
